import { Injectable, Injector } from "@angular/core";
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpErrorResponse } from "@angular/common/http";
import { Router } from "@angular/router";
import { Observable, throwError } from "rxjs";
import { catchError } from "rxjs/operators";
import { StorageService } from "../services/storage.service";
import { NodeService } from "../services/node.service";

/**
 * Endpoint Node pubblici realmente chiamati dal frontend (match ESATTO sul
 * pathname): nessun Bearer allegato e nessun redirect su 401.
 * Prima era `url.includes('/login')` & co., che matchava qualunque URL
 * contenente la sottostringa (anche host terzi / query string).
 */
const PUBLIC_NODE_PATHS = new Set<string>([
  "/users/login",
  "/clienti/login",
  "/users/recover",
  "/clienti/exchange-link",
  "/users/LinkAccess",
]);

/**
 * Origin Node raggiungibili con HttpClient oltre a `NodeService.baseUrl`:
 * `GlobalService` (upload grafici `/quest/upload/charts`, requireAuth) usa
 * questi host hardcoded anche da localhost.
 */
const EXTRA_NODE_ORIGINS = [
  "https://node.amajorsb.com",
  "https://collnode.amajorsb.com",
];

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private handling401 = false;
  private nodeOrigins: Set<string> | null = null;

  // NodeService iniettato in modo lazy via Injector: iniettarlo nel costruttore
  // crea una dipendenza ciclica (NodeService → HttpClient → HTTP_INTERCEPTORS).
  constructor(private storage: StorageService, private router: Router, private injector: Injector) {}

  /** Origin del backend Node (da NodeService.baseUrl, risolto per hostname). */
  private getNodeOrigins(): Set<string> {
    if (!this.nodeOrigins) {
      const origins = new Set<string>(EXTRA_NODE_ORIGINS);
      try {
        const base = this.injector.get(NodeService).baseUrl;
        if (base) origins.add(new URL(base).origin);
      } catch (err) {
        console.debug("[AuthInterceptor] node baseUrl non risolto", err);
      }
      this.nodeOrigins = origins;
    }
    return this.nodeOrigins;
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    let reqUrl: URL | null = null;
    try {
      reqUrl = new URL(req.url, window.location.origin);
    } catch {
      reqUrl = null;
    }
    // Bearer SOLO verso il backend Node: asset (i18n, icone), Firebase e host
    // terzi non devono mai ricevere il JWT.
    const isNodeBackend = !!reqUrl && this.getNodeOrigins().has(reqUrl.origin);
    const isPublic = !!reqUrl && PUBLIC_NODE_PATHS.has(reqUrl.pathname);

    // Flusso di compilazione anonimo (candidato): non deve mai portarsi
    // dietro un JWT admin/cliente residuo in localStorage da un login
    // precedente sullo stesso browser. Se allegato e scaduto, il 401
    // faceva scattare un redirect a /admin/login che vinceva la race
    // contro la navigazione a /compilazione/conclusione dopo l'invio.
    const currentUrl = this.router.url;
    const isAnonymousCompilationFlow =
      currentUrl.startsWith('/compilazione') ||
      currentUrl.startsWith('/public/') ||
      currentUrl.startsWith('/new-compilation/');

    let outReq = req;
    if (isNodeBackend && !req.headers.has('Authorization') && !isPublic && !isAnonymousCompilationFlow) {
      const token = this.storage.getApiToken();
      if (token && token.split('.').length === 3) {
        outReq = req.clone({ setHeaders: { Authorization: `Bearer ${token}` } });
      }
    }

    // Se la richiesta parte senza token (es. flow /public/:slug → email
    // risultati fire-and-forget) un eventuale 401 NON deve mandare l'utente
    // sul login: non c'è alcuna sessione da invalidare. Redirigiamo solo
    // quando il token c'era ed è stato rifiutato (sessione scaduta).
    const hadAuth = outReq.headers.has('Authorization');

    return next.handle(outReq).pipe(
      catchError((err: HttpErrorResponse) => {
        if (err.status === 401 && !isPublic && !isAnonymousCompilationFlow && !this.handling401 && hadAuth) {
          this.handling401 = true;
          this.storage.clearApiToken();
          localStorage.removeItem("LoggedInUser");
          sessionStorage.removeItem("LoggedInUser");

          const isCliente = this.router.url.startsWith('/cliente');
          const target = isCliente ? ['/login'] : ['/admin/login'];
          this.router.navigate(target).finally(() => { this.handling401 = false; });
        }
        return throwError(() => err);
      })
    );
  }
}
